import LoadingIndicator from '@orn/shared-web/components/LoadingIndicator';
import isPinnedToBottom from '@orn/shared-web/utils/scrollPin';
import * as React from 'react';

import { AssistantInput, AssistantMessage, ConnectionIndicator } from '../../components/assistant';
import { buildTimestampLabels, isRenderableMessage } from '../../lib/assistant/messageDisplay';

import AssistantPaneEmptyState from './AssistantPaneEmptyState';

import type { ConnectionStatus, IConversationMessage } from '../../hooks/useAssistantWebSocket';
import type { IObservationDocument } from '@orn/global/assistant/snapshotMessage.js';

interface IAssistantPaneViewProps {
  messages: IConversationMessage[];
  isProcessing: boolean;
  /** Forwarded to {@link AssistantInput}: true while the live turn is abortable → button is a stop button. */
  isAbortable: boolean;
  isRehydrating: boolean;
  connectionStatus: ConnectionStatus;
  error: string | null;
  /**
   * Forwarded to {@link AssistantInput}. `attachments` is populated by the
   * composer when the user attached documents to the turn; the hook derives
   * the wire `attachmentDocumentIds` from `attachments.map(a => a.id)` and
   * reuses the same array for the locally-echoed bubble.
   */
  onSend: (content: string, attachments?: IObservationDocument[]) => void;
  /** Forwarded to {@link AssistantInput}: interrupt the in-flight turn. */
  onStop: () => void;
}

/**
 * Pure presentational view for the assistant pane. Takes the
 * `useAssistantWebSocket` hook's return shape as props so Storybook stories
 * and visual-verification subagents can render every state from fixture
 * data without mocking the hook.
 *
 * Timestamps render as centered separators above the bubble that opens each
 * wall-clock minute (see {@link buildTimestampLabels}); empty turns (no
 * content, no attachments) are skipped; tool-call detail is not rendered —
 * a turn in flight shows only a spinner bubble, and the details are surfaced
 * to the console behind the `debug-assistant` gate by {@link AssistantPane}.
 */
const AssistantPaneView = ({
  messages,
  isProcessing,
  isAbortable,
  isRehydrating,
  connectionStatus,
  error,
  onSend,
  onStop,
}: IAssistantPaneViewProps) => {
  const scrollContainerRef = React.useRef<HTMLDivElement>(null);
  // Whether the user is pinned to the bottom. Updated on every scroll so the
  // auto-follow reflects the user's latest intent — scrolling up to read history
  // mid-stream parks this `false` and stops the follow until they return.
  const isPinnedRef = React.useRef(true);

  const handleScroll = React.useCallback((event: React.UIEvent<HTMLDivElement>) => {
    isPinnedRef.current = isPinnedToBottom(event.currentTarget);
  }, []);

  // One pinned-aware auto-follow for both discrete new bubbles (length changes)
  // and token-level streaming, where a bubble's text grows in place with no
  // length change. Key off total content length, not the tail bubble's — the
  // streaming bubble is not always last (mid-turn queued user bubbles sit below
  // it), so a tail-only key would stall the follow. Jump instantly — smooth
  // scrolling would pile up animations at token rate.
  const totalContentLength = messages.reduce((sum, m) => sum + m.content.length, 0);
  React.useEffect(() => {
    if (!isPinnedRef.current) {
      return;
    }
    const container = scrollContainerRef.current;
    if (container) {
      container.scrollTop = container.scrollHeight;
    }
  }, [messages.length, totalContentLength]);

  const isConnecting = connectionStatus === 'connecting';
  const isConnected = connectionStatus === 'connected';
  const hasError = connectionStatus === 'error' || !!error;

  // While a round's text streams, its "working" affordance lives inline at the
  // bottom of the growing bubble (see `AssistantMessage`), so the standalone
  // spinner below is suppressed. Between rounds (tools running, or before the
  // first token) no bubble is streaming and the standalone spinner shows.
  const isStreaming = messages.some((m) => m.isStreaming === true);

  const timestampLabels = React.useMemo(() => buildTimestampLabels(messages), [messages]);

  // Split the transcript so the thinking spinner sits between in-flight
  // messages (the current turn) and queued messages (the user typed them
  // mid-turn; they wait for the current turn's reply before being batched
  // into the next turn). Confirmed + in-flight rows go above the spinner;
  // queued rows go below.
  //
  // The split point is the first row that is pending AND not in flight —
  // i.e. the first queued bubble. Without this, B and C typed while A is
  // being processed would visually sit above the spinner, misleading the
  // user into thinking the assistant is already working on C when it is
  // still on A.
  const firstQueuedIndex = messages.findIndex((m) => m.isPending === true && m.isInFlight !== true);
  const messagesAboveThinking = firstQueuedIndex === -1 ? messages : messages.slice(0, firstQueuedIndex);
  const messagesBelowThinking = firstQueuedIndex === -1 ? [] : messages.slice(firstQueuedIndex);

  const renderMessageRow = (message: IConversationMessage) => {
    if (!isRenderableMessage(message)) {
      return null;
    }
    const timestampLabel = timestampLabels.get(message.id);
    return (
      <div key={message.id} className="flex flex-col gap-1">
        {timestampLabel && <div className="text-tertiary py-2 text-center text-xs">{timestampLabel}</div>}
        <AssistantMessage message={message} />
      </div>
    );
  };

  const inputPlaceholder = isConnecting
    ? 'Connecting...'
    : isConnected
      ? 'Ask about your finances...'
      : 'Reconnecting...';

  return (
    <div className="flex h-full w-full flex-col">
      {/* Header — to start a fresh session, the user types the `/reset` command. */}
      <div className="border-secondary flex items-center gap-3 border-b px-6 py-4">
        <div className="text-primary text-xl font-semibold">Mantle Assistant</div>
        <ConnectionIndicator status={connectionStatus} />
      </div>

      {/* Messages area */}
      <div ref={scrollContainerRef} onScroll={handleScroll} className="flex-1 overflow-y-auto">
        <div className="mx-auto flex max-w-3xl flex-col gap-4 p-6">
          {/* Error message */}
          {hasError && (
            <div className="bg-error-primary text-error-primary rounded-lg p-4 text-sm">
              {error ?? 'Connection error. Attempting to reconnect...'}
            </div>
          )}

          {/* Loading spinner — covers both the cold-connect window (WS not
              yet open, often gated behind AuthProvider resolving session)
              AND the post-open rehydration handshake
              (`transcript_snapshot` + `assistant_ready`). Suppressed once
              local messages populate so a reconnect with stale messages
              keeps showing them until the fresh snapshot replaces. */}
          {(isConnecting || isRehydrating) && messages.length === 0 && !hasError && (
            <div className="flex flex-col items-center justify-center gap-3 py-16">
              <LoadingIndicator size="md" />
              <div className="text-tertiary text-sm">Loading your conversation…</div>
            </div>
          )}

          {/* Empty state */}
          {messages.length === 0 && !isProcessing && !hasError && !isConnecting && !isRehydrating && (
            <AssistantPaneEmptyState />
          )}

          {/* Messages up to and including the message currently being
              processed (the oldest pending). */}
          {messagesAboveThinking.map(renderMessageRow)}

          {/* A turn in flight with no text currently streaming: a plain assistant
              bubble with a spinner, positioned between the message the server is
              working on and any queued follow-ups. Suppressed while a round's
              text streams — that round's spinner lives inline in its growing
              bubble. Tool-call detail is intentionally omitted. */}
          {isProcessing && !isStreaming && (
            <div className="flex w-full flex-col items-start">
              <div className="bg-secondary rounded-2xl px-4 py-3">
                <LoadingIndicator type="line-spinner" size="xs" />
              </div>
            </div>
          )}

          {/* User messages queued behind the current turn. Each is an
              optimistic bubble that hasn't yet been picked up by the server
              orchestrator's FIFO. */}
          {messagesBelowThinking.map(renderMessageRow)}
        </div>
      </div>

      {/* Input area */}
      <div className="border-secondary border-t p-4">
        <div className="mx-auto max-w-3xl">
          {/* Input is always enabled. The hook queues sends when the WS is
              not OPEN + assistant_ready (drained on the next ready frame),
              and the server orchestrator FIFOs mid-turn user_messages on
              its own processing chain, so backpressure does not need a UI
              gate. The placeholder still reflects the underlying network
              state. */}
          <AssistantInput onSend={onSend} isAbortable={isAbortable} onStop={onStop} placeholder={inputPlaceholder} />
        </div>
      </div>
    </div>
  );
};

export default AssistantPaneView;
